import React, { useState } from 'react';
import type { Area, Ambulance } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

interface AreaMappingProps {
  areas: Area[];
  ambulances: Ambulance[];
  loading: boolean;
  getStatusChipStyle: (status: string) => React.CSSProperties;
  formatAmbulanceStatus: (status: string) => string;
  onUpdate: (areaId: string, ambulanceIds: string[]) => Promise<void>;
}

const AreaMapping: React.FC<AreaMappingProps> = ({
  areas,
  ambulances,
  loading,
  getStatusChipStyle,
  formatAmbulanceStatus,
  onUpdate,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedAmbulanceIds, setSelectedAmbulanceIds] = useState<string[]>([]);
  const [showConfirmDrawer, setShowConfirmDrawer] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Search states
  const [showAreaSearch, setShowAreaSearch] = useState(false);
  const [areaSearchTerm, setAreaSearchTerm] = useState('');
  const [showAmbulanceSearch, setShowAmbulanceSearch] = useState(false);
  const [ambulanceSearchTerm, setAmbulanceSearchTerm] = useState('');
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  const selectedArea = selectedAreaId
    ? areas.find((a) => a._id === selectedAreaId)
    : null;

  // Check if there are changes in ambulance assignments
  const hasChanges = () => {
    if (!selectedArea) return false;

    const currentAmbulanceIds = (selectedArea.spec.ambulances?.map((a) => a.ambulanceId) || []).sort();
    const newAmbulanceIds = [...selectedAmbulanceIds].sort();
    
    return (
      currentAmbulanceIds.length !== newAmbulanceIds.length ||
      currentAmbulanceIds.some((id, index) => id !== newAmbulanceIds[index])
    );
  };

  // Sort areas by name
  const sortedAreas = [...areas].sort((a, b) => 
    a.metadata.name.localeCompare(b.metadata.name)
  );

  // Filter areas by name
  const filteredAreas = sortedAreas.filter((area) => {
    if (!areaSearchTerm.trim()) return true;
    const searchLower = areaSearchTerm.toLowerCase();
    const name = area.metadata.name.toLowerCase();
    return name.includes(searchLower);
  });

  // Sort ambulances by status: Available > On Duty > Off Duty > Maintenance
  const getStatusPriority = (status: string): number => {
    const statusNormalized = status.toLowerCase().replace(/[-_\s]/g, '');
    if (statusNormalized === 'available') return 1;
    if (statusNormalized === 'onduty') return 2;
    if (statusNormalized === 'offduty') return 3;
    if (statusNormalized === 'maintenance') return 4;
    return 5;
  };

  const sortedAmbulances = [...ambulances].sort((a, b) => {
    const priorityA = getStatusPriority(a.spec.status);
    const priorityB = getStatusPriority(b.spec.status);
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    return a.metadata.name.localeCompare(b.metadata.name);
  });

  // Filter ambulances by name and license plate
  const filteredAmbulances = sortedAmbulances.filter((ambulance) => {
    if (!ambulanceSearchTerm.trim()) return true;
    const searchLower = ambulanceSearchTerm.toLowerCase();
    const name = ambulance.metadata.name.toLowerCase();
    const licensePlate = ambulance.spec.licensePlate.toLowerCase();
    return name.includes(searchLower) || licensePlate.includes(searchLower);
  });

  const handleAreaClick = (area: Area) => {
    if (selectedAreaId === area._id) {
      setSelectedAreaId(null);
      setSelectedAmbulanceIds([]);
    } else {
      setSelectedAreaId(area._id);
      // Start with assigned ambulances selected by default
      setSelectedAmbulanceIds(area.spec.ambulances?.map((a) => a.ambulanceId) || []);
    }
  };

  const handleAmbulanceClick = (ambulanceId: string) => {
    if (!selectedAreaId) return;
    
    // Toggle ambulance selection: if already selected, uncheck it; otherwise, check it
    // Use functional update to ensure we have the latest state
    setSelectedAmbulanceIds((prevIds) => {
      const currentIndex = prevIds.indexOf(ambulanceId);
      if (currentIndex >= 0) {
        // Uncheck: remove from selection - create new array to ensure React detects change
        const newIds = [...prevIds];
        newIds.splice(currentIndex, 1);
        return newIds;
      } else {
        // Check: add to selection - create new array to ensure React detects change
        return [...prevIds, ambulanceId];
      }
    });
  };

  const handleUpdate = async () => {
    if (!selectedAreaId) return;
    
    setIsUpdating(true);
    try {
      await onUpdate(selectedAreaId, selectedAmbulanceIds);
      setShowConfirmDrawer(false);
    } catch (error) {
      console.error('Failed to update area:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Calculate ambulance counts by status
  const ambulanceCounts = filteredAmbulances.reduce((acc, ambulance) => {
    const status = ambulance.spec.status?.toLowerCase().replace(/[-_\s]/g, '') || 'unknown';
    if (status === 'available') acc.available = (acc.available || 0) + 1;
    else if (status === 'onduty') acc.onDuty = (acc.onDuty || 0) + 1;
    else if (status === 'offduty') acc.offDuty = (acc.offDuty || 0) + 1;
    else if (status === 'maintenance') acc.maintenance = (acc.maintenance || 0) + 1;
    else acc.unknown = (acc.unknown || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div
      className="card"
      style={{
        height: 'calc(100vh - 215px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <div className="card-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="loading">Loading data...</div>
        ) : areas.length === 0 ? (
          <div className="empty-state">
            <h3>No areas yet</h3>
            <p>Create your first area to configure ambulance mapping</p>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '32px',
                alignItems: 'stretch',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              {/* Area list column */}
              <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '16px',
                  paddingBottom: '12px',
                  borderBottom: `2px solid ${isDark ? '#94a3b8' : '#cbd5e1'}`,
                }}>
                  <h3 className="card-title" style={{ margin: 0, fontSize: '19px', fontWeight: 700, paddingBottom: 0 }}>
                    Areas
                  </h3>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {showAreaSearch && (
                      <input
                        type="text"
                        placeholder="Search by name..."
                        value={areaSearchTerm}
                        onChange={(e) => setAreaSearchTerm(e.target.value)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid',
                          fontSize: '14px',
                          width: '220px',
                          flex: '0 0 220px',
                          outline: 'none',
                          backgroundColor: isDark ? '#1e293b' : '#ffffff',
                          color: isDark ? '#f1f5f9' : '#1e293b',
                        }}
                        className="mapping-item-border"
                        autoFocus
                      />
                    )}
                    <button
                      onClick={() => {
                        setShowAreaSearch(!showAreaSearch);
                        setAreaSearchTerm('');
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isDark ? '#cbd5e1' : '#475569',
                      }}
                      title={showAreaSearch ? "Close search" : "Search"}
                    >
                      {showAreaSearch ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"></circle>
                          <path d="m21 21-4.35-4.35"></path>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  overflowX: 'hidden',
                  minHeight: 0,
                  maxHeight: '100%',
                  paddingRight: '4px',
                }}>
                  {filteredAreas.map((area) => {
                    const isSelected = selectedAreaId === area._id;
                    const ambulanceCount = area.spec.ambulances?.length || 0;
                    return (
                      <div
                        key={area._id}
                        onClick={() => handleAreaClick(area)}
                        className={isSelected ? 'mapping-item-selected' : 'mapping-item-border'}
                        style={{
                          padding: '12px 14px',
                          borderRadius: '10px',
                          border: isSelected
                            ? `2px solid ${isDark ? '#94a3b8' : '#667eea'}`
                            : `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                          marginBottom: '10px',
                          fontSize: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          backgroundColor: isSelected
                            ? (isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(102, 126, 234, 0.15)')
                            : (isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(102, 126, 234, 0.05)'),
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: isSelected 
                            ? (isDark ? '0 4px 12px rgba(148, 163, 184, 0.3)' : '0 4px 12px rgba(102, 126, 234, 0.25)')
                            : '0 2px 4px rgba(0, 0, 0, 0.05)',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                          } else {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          if (!isSelected) {
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                          } else {
                            e.currentTarget.style.boxShadow = isDark ? '0 4px 12px rgba(148, 163, 184, 0.3)' : '0 4px 12px rgba(102, 126, 234, 0.25)';
                          }
                        }}
                      >
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: isDark ? '#94a3b8' : '#667eea',
                            zIndex: 2,
                          }} />
                        )}
                        {!isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: isDark ? '#94a3b8' : '#667eea',
                            zIndex: 1,
                          }} />
                        )}
                        <div style={{ fontWeight: 600, fontSize: '16px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isDark ? '#ffffff' : undefined }}>
                          {area.metadata.name}
                        </div>
                        <div style={{ fontSize: '14px', color: isDark ? '#cbd5e1' : '#64748b', whiteSpace: 'nowrap' }}>
                          {ambulanceCount} {ambulanceCount === 1 ? 'ambulance' : 'ambulances'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Ambulance list column */}
              <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '16px',
                  paddingBottom: '12px',
                  borderBottom: `2px solid ${isDark ? '#94a3b8' : '#cbd5e1'}`,
                }}>
                  <h3 className="card-title" style={{ margin: 0, fontSize: '19px', fontWeight: 700, paddingBottom: 0 }}>
                    Ambulances
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {ambulanceCounts.available > 0 && (
                      <span 
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            text: `${ambulanceCounts.available} Ambulance${ambulanceCounts.available !== 1 ? 's' : ''} ${ambulanceCounts.available === 1 ? 'is' : 'are'} Available`,
                            x: rect.left + rect.width / 2,
                            y: rect.top - 5,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                        style={{ 
                          fontSize: '12px', 
                          padding: '3px 6px',
                          borderRadius: '10px',
                          background: 'rgba(16, 185, 129, 0.2)',
                          color: '#10b981',
                          fontWeight: 600,
                          cursor: 'help',
                        }}
                      >
                        {ambulanceCounts.available}
                      </span>
                    )}
                    {ambulanceCounts.onDuty > 0 && (
                      <span 
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            text: `${ambulanceCounts.onDuty} Ambulance${ambulanceCounts.onDuty !== 1 ? 's' : ''} ${ambulanceCounts.onDuty === 1 ? 'is' : 'are'} On Duty`,
                            x: rect.left + rect.width / 2,
                            y: rect.top - 5,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                        style={{ 
                          fontSize: '12px', 
                          padding: '3px 6px',
                          borderRadius: '10px',
                          background: 'rgba(239, 68, 68, 0.2)',
                          color: '#ef4444',
                          fontWeight: 600,
                          cursor: 'help',
                        }}
                      >
                        {ambulanceCounts.onDuty}
                      </span>
                    )}
                    {ambulanceCounts.offDuty > 0 && (
                      <span 
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            text: `${ambulanceCounts.offDuty} Ambulance${ambulanceCounts.offDuty !== 1 ? 's' : ''} ${ambulanceCounts.offDuty === 1 ? 'is' : 'are'} Off Duty`,
                            x: rect.left + rect.width / 2,
                            y: rect.top - 5,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                        style={{ 
                          fontSize: '12px', 
                          padding: '3px 6px',
                          borderRadius: '10px',
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b',
                          fontWeight: 600,
                          cursor: 'help',
                        }}
                      >
                        {ambulanceCounts.offDuty}
                      </span>
                    )}
                    {ambulanceCounts.maintenance > 0 && (
                      <span 
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            text: `${ambulanceCounts.maintenance} Ambulance${ambulanceCounts.maintenance !== 1 ? 's' : ''} ${ambulanceCounts.maintenance === 1 ? 'is' : 'are'} in Maintenance`,
                            x: rect.left + rect.width / 2,
                            y: rect.top - 5,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                        style={{ 
                          fontSize: '12px', 
                          padding: '3px 6px',
                          borderRadius: '10px',
                          background: 'rgba(100, 116, 139, 0.2)',
                          color: '#64748b',
                          fontWeight: 600,
                          cursor: 'help',
                        }}
                      >
                        {ambulanceCounts.maintenance}
                      </span>
                    )}
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {showAmbulanceSearch && (
                      <input
                        type="text"
                        placeholder="Search by name or license plate..."
                        value={ambulanceSearchTerm}
                        onChange={(e) => setAmbulanceSearchTerm(e.target.value)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid',
                          fontSize: '14px',
                          width: '220px',
                          flex: '0 0 220px',
                          outline: 'none',
                          backgroundColor: isDark ? '#1e293b' : '#ffffff',
                          color: isDark ? '#f1f5f9' : '#1e293b',
                        }}
                        className="mapping-item-border"
                        autoFocus
                      />
                    )}
                    <button
                      onClick={() => {
                        setShowAmbulanceSearch(!showAmbulanceSearch);
                        setAmbulanceSearchTerm('');
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isDark ? '#cbd5e1' : '#475569',
                      }}
                      title={showAmbulanceSearch ? "Close search" : "Search"}
                    >
                      {showAmbulanceSearch ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"></circle>
                          <path d="m21 21-4.35-4.35"></path>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  overflowX: 'hidden',
                  minHeight: 0,
                  maxHeight: '100%',
                  paddingRight: '4px',
                }}>
                  {filteredAmbulances.map((ambulance) => {
                    const statusChipStyle = getStatusChipStyle(ambulance.spec.status);
                    const isSelected = selectedAmbulanceIds.includes(ambulance._id);
                    const statusColor = statusChipStyle.color || '#64748b';
                    let statusBackground = statusChipStyle.backgroundColor || `${statusColor}1A`;
                    if (statusBackground.length === 9 && statusBackground.startsWith('#')) {
                      const r = parseInt(statusBackground.slice(1, 3), 16);
                      const g = parseInt(statusBackground.slice(3, 5), 16);
                      const b = parseInt(statusBackground.slice(5, 7), 16);
                      statusBackground = `rgba(${r}, ${g}, ${b}, 0.2)`;
                    } else if (!statusBackground.includes('rgba') && !statusBackground.includes('rgb')) {
                      const hex = statusColor.replace('#', '');
                      const r = parseInt(hex.slice(0, 2), 16);
                      const g = parseInt(hex.slice(2, 4), 16);
                      const b = parseInt(hex.slice(4, 6), 16);
                      statusBackground = `rgba(${r}, ${g}, ${b}, 0.2)`;
                    }
                    return (
                      <div
                        key={ambulance._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedAreaId) {
                            handleAmbulanceClick(ambulance._id);
                          }
                        }}
                        className={isSelected ? 'mapping-item-selected' : 'mapping-item-border'}
                        style={{
                          padding: '12px 14px',
                          borderRadius: '10px',
                          border: isSelected
                            ? `2px solid ${isDark ? '#94a3b8' : '#667eea'}`
                            : `1px solid ${statusColor}`,
                          marginBottom: '10px',
                          fontSize: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: isSelected
                            ? (isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(102, 126, 234, 0.15)')
                            : statusBackground,
                          cursor: selectedAreaId ? 'pointer' : 'default',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: isSelected
                            ? '0 4px 12px rgba(102, 126, 234, 0.25)' 
                            : '0 2px 4px rgba(0, 0, 0, 0.05)',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                        onMouseEnter={(e) => {
                          if (selectedAreaId && !isSelected) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                          } else if (isSelected) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = isDark ? '0 6px 16px rgba(148, 163, 184, 0.55)' : '0 6px 16px rgba(102, 126, 234, 0.5)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          if (!isSelected) {
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                          } else {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.25)';
                          }
                        }}
                      >
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: isDark ? '#94a3b8' : '#667eea',
                            zIndex: 2,
                          }} />
                        )}
                        {!isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: statusColor,
                            zIndex: 1,
                          }} />
                        )}
                        <div style={{ fontWeight: 600, fontSize: '16px', width: '50%', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '4px', color: isDark ? '#ffffff' : undefined }}>
                          {ambulance.metadata.name}
                        </div>
                        <div style={{ width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0, padding: '0 4px', gap: '4px' }}>
                          <code style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            background: 'rgba(102, 126, 234, 0.1)',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            fontWeight: 600,
                            border: '1px solid rgba(102, 126, 234, 0.2)',
                            whiteSpace: 'nowrap',
                            color: isDark ? '#ffffff' : undefined,
                          }}>
                            {ambulance.spec.licensePlate}
                          </code>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {selectedAreaId && (
              <div className="mapping-update-section" style={{ 
                marginTop: '20px', 
                paddingTop: '20px',
                borderTop: `2px solid ${isDark ? '#94a3b8' : '#cbd5e1'}`,
                width: '100%',
              }}>
                <button
                  onClick={() => setShowConfirmDrawer(true)}
                  disabled={!hasChanges()}
                  className="btn-submit"
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    fontSize: '17px',
                    fontWeight: 600,
                    cursor: hasChanges() ? 'pointer' : 'not-allowed',
                    opacity: hasChanges() ? 1 : 0.5,
                    borderRadius: '10px',
                    border: 'none',
                    background: hasChanges() 
                      ? (isDark ? '#667eea' : '#667eea')
                      : (isDark ? '#475569' : '#cbd5e1'),
                    color: 'white',
                    boxShadow: hasChanges() 
                      ? (isDark ? '0 4px 15px rgba(102, 126, 234, 0.4)' : '0 4px 15px rgba(102, 126, 234, 0.4)')
                      : (isDark ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)'),
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (hasChanges()) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isDark ? '0 6px 20px rgba(102, 126, 234, 0.5)' : '0 6px 20px rgba(102, 126, 234, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = hasChanges() 
                      ? (isDark ? '0 4px 15px rgba(102, 126, 234, 0.4)' : '0 4px 15px rgba(102, 126, 234, 0.4)')
                      : (isDark ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)');
                  }}
                >
                  Update
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Drawer */}
      {showConfirmDrawer && selectedArea && (
        <div className="drawer-overlay" onClick={() => setShowConfirmDrawer(false)}>
          <div
            className="drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-header">
              <h3 className="drawer-title">Area Mapping Update</h3>
              <button className="drawer-close" onClick={() => setShowConfirmDrawer(false)}>
                ×
              </button>
            </div>

            <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <div style={{ 
                padding: '12px', 
                borderRadius: '6px', 
                fontSize: '20px',
                flexShrink: 0,
                marginBottom: '20px',
                fontWeight: 600,
                color: isDark ? '#ffffff' : undefined,
              }}>
                Are you sure you want to update the ambulance mapping for this area?
              </div>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                paddingRight: '8px',
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>Area Details</h4>
                  <div style={{ padding: '12px', borderRadius: '6px' }}>
                    <div>
                      <strong>Name:</strong> {selectedArea.metadata.name}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>Current Mapping</h4>
                  <div style={{ padding: '12px', borderRadius: '6px' }}>
                    {selectedArea.spec.ambulances && selectedArea.spec.ambulances.length > 0 ? (
                      <div>
                        {selectedArea.spec.ambulances.map((amb, index) => (
                          <div key={index} style={{ marginBottom: '4px' }}>
                            {amb.name} ({amb.licensePlate})
                          </div>
                        ))}
                      </div>
                    ) : (
                      ' No ambulances assigned'
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600 }}>New Mapping</h4>
                  <div style={{ padding: '12px', borderRadius: '6px' }}>
                    {selectedAmbulanceIds.length > 0 ? (
                      <div>
                        {selectedAmbulanceIds.map((id) => {
                          const ambulance = ambulances.find((a) => a._id === id);
                          return ambulance ? (
                            <div key={id} style={{ marginBottom: '4px' }}>
                              {ambulance.metadata.name} ({ambulance.spec.licensePlate})
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      ' No ambulances assigned'
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="drawer-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowConfirmDrawer(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleUpdate}
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Tooltip */}
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translate(-50%, -100%)',
            backgroundColor: isDark ? '#1e293b' : '#0f172a',
            color: '#ffffff',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            zIndex: 10000,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {tooltip.text}
          <div
            style={{
              position: 'absolute',
              bottom: '-5px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: `5px solid ${isDark ? '#1e293b' : '#0f172a'}`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AreaMapping;

